import { Box, Modal, IconButton, Tooltip } from "@mui/material";
import { Close, FileDownload, Link, GridView } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CopyToClipboard from "react-copy-to-clipboard";
import { ButtonV1 } from "../../components/atoms/button";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import { useGetCreativeByIdQuery } from "../../api/creative";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import "../../components/molecules/PdfViewer.css";

export default function Presentation() {
    const [copy, setCopy] = useState(false);
    const [showThumbnails, setShowThumbnails] = useState(false);
    const [URLSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const creativeId = URLSearchParams.get("creativeId") || "";
    const { data: creativeData, isLoading, isError } = useGetCreativeByIdQuery(creativeId);

    // Get data from API response
    const pdfUrl = creativeData?.data?.pptDetails?.pdfLink || "";
    const pptUrl = creativeData?.data?.pptDetails?.pptLink || "";
    const title = creativeData?.data?.newTitle || creativeData?.data?.title || "Presentation";
    const stage = creativeData?.data?.stage || "";
    const dtLevel = creativeData?.data?.dt || "";

    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { CurrentPageInput, GoToNextPage, GoToPreviousPage, NumberOfPages } =
        pageNavigationPluginInstance;

    const thumbnailPluginInstance = thumbnailPlugin();
    const { Thumbnails } = thumbnailPluginInstance;

    const handleClose = () => {
        navigate(-1); // Go back to previous page
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'Escape':
                    handleClose();
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const style = {
        position: "absolute" as "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "95vw",
        maxWidth: "1400px",
        height: "90vh",
        bgcolor: "background.paper",
        borderRadius: "1.5rem",
        boxShadow: 24,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    };

    // Create shareable URL using creativeId
    const shareableUrl = `${window.location.origin}/presentation?creativeId=${creativeId}`;

    // Show loading state
    if (isLoading) {
        return (
            <div className="bg-black h-[100vh] flex justify-center items-center">
                <div className="text-white text-xl">Loading...</div>
            </div>
        );
    }

    // Show error state
    if (isError) {
        return (
            <Modal
                open={true}
                onClose={handleClose}
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0, 0, 0, 0.95) !important",
                    }
                }}
            >
                <Box sx={style}>
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <h2 className="text-2xl mb-4 text-red-600">Error loading presentation</h2>
                            <p className="text-gray-600 mb-4">Unable to fetch the presentation data</p>
                            <ButtonV1 onClick={handleClose}>Close</ButtonV1>
                        </div>
                    </div>
                </Box>
            </Modal>
        );
    }

    if (!pdfUrl || pdfUrl.trim() === "") {
        return (
            <Modal
                open={true}
                onClose={handleClose}
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0, 0, 0, 0.95) !important",
                    }
                }}
            >
                <Box sx={style}>
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <h2 className="text-2xl mb-4">No presentation found</h2>
                            <ButtonV1 onClick={handleClose}>Close</ButtonV1>
                        </div>
                    </div>
                </Box>
            </Modal>
        );
    }

    return (
        <Modal
            open={true}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            sx={{
                "& .MuiBackdrop-root": {
                    backgroundColor: "rgba(0, 0, 0, 0.95) !important",
                }
            }}
        >
            <Box sx={style}>
                {/* Header */}
                <div className="pdf-viewer flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-2">
                        <Tooltip title={showThumbnails ? "Hide thumbnails" : "Show thumbnails"}>
                            <IconButton
                                onClick={() => setShowThumbnails((prev) => !prev)}
                                size="small"
                            >
                                <GridView />
                            </IconButton>
                        </Tooltip>
                        <div className="flex gap-1 items-center textsPdf font-sans">
                            <GoToPreviousPage />
                            <CurrentPageInput /> <span>|</span> <span><NumberOfPages /></span>
                            <GoToNextPage />
                        </div>
                    </div>
                </div>

                {/* PDF Viewer + Thumbnails */}
                <div className="flex flex-1 overflow-hidden bg-gray-100">
                    {/* Thumbnails (left) */}
                    <div
                        className={`transition-all duration-300 border-r border-gray-200 overflow-auto hidden md:block ${showThumbnails ? "w-1/5" : "w-0"
                            }`}
                    >
                        {showThumbnails && <Thumbnails />}
                    </div>

                    {/* Main Viewer (always full width, fills remaining space) */}
                    <div
                        className={`flex-1 transition-all duration-300 ${showThumbnails ? "w-4/5 max-w-[80%]" : "w-full"
                            }`}
                    >
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <Viewer
                                fileUrl={pdfUrl}
                                defaultScale={0.75}
                                plugins={[pageNavigationPluginInstance, thumbnailPluginInstance]}
                            />
                        </Worker>
                    </div>
                </div>

                {/* Footer */}
                <div className="w-full flex gap-2 justify-between items-center bg-gray-50 border-t border-gray-200">
                    <div className="flex-1 flex flex-col px-4 py-2">
                        {(stage || dtLevel) && (
                            <div className="flex items-center gap-1 font-medium text-sm text-gray-700">
                                <span
                                    className={`w-2 h-2 rounded-full ${stage === "postsale" ? "bg-black" : "bg-gray-500"
                                        }`}
                                ></span>
                                {stage}
                                {dtLevel ? ` | ${dtLevel}` : ""}
                            </div>
                        )}
                        <h1 className="truncate text-xl font-medium">{title}</h1>
                    </div>

                    <div className="flex gap-3 px-4">
                        <ButtonV1>
                            <CopyToClipboard
                                text={shareableUrl}
                                onCopy={() => setCopy(true)}
                            >
                                {copy ? (
                                    <p className="capitalize text-xs">Copied</p>
                                ) : (
                                    <Link className="text-white" />
                                )}
                            </CopyToClipboard>
                        </ButtonV1>
                        {pdfUrl && pdfUrl.trim() !== "" && (
                            <ButtonV1>
                                <a
                                    href={pdfUrl}
                                    onClick={() => setCopy(false)}
                                    download
                                    className="flex items-center justify-center"
                                >
                                    <FileDownload className="text-white" />
                                </a>
                            </ButtonV1>
                        )}
                    </div>
                </div>
            </Box>
        </Modal>
    );
}

